import React, { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Col, Input, Row } from "reactstrap";
import { MessageSquare, Paperclip } from "react-feather";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { formatAmount } from "../../../utils/formatMoney";
import EntryNotesPanel from "./EntryNotesPanel";
import EntryAttachmentsPanel from "./EntryAttachmentsPanel";
import { can, formatShortDate, operationLabel, PERMISSIONS } from "./glUtils";
import { TABLE_STYLES, useStickyTableHeader } from "./tableLayout";

function CategoryEditor({ entry, categories, canEdit }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(entry.category || "");
  const mutation = useMutation({
    mutationFn: () => GeneralLedgerService.updateEntry(entry.id, { category: value.trim() || null }),
    onSuccess: () => {
      toast.success("Category saved");
      queryClient.invalidateQueries({ queryKey: ["glEntries"] });
      queryClient.invalidateQueries({ queryKey: ["glMeta"] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not save the category.")),
  });

  if (!canEdit) return <span>{entry.category || "—"}</span>;

  return (
    <div className="d-flex gap-2">
      <Input
        bsSize="sm"
        list={`gl-categories-${entry.id}`}
        maxLength={64}
        value={value}
        placeholder="e.g. Payroll, Rent, Royalties"
        onChange={(e) => setValue(e.target.value)}
      />
      <datalist id={`gl-categories-${entry.id}`}>
        {(categories || []).map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <Button
        size="sm"
        color="primary"
        disabled={mutation.isPending || value === (entry.category || "")}
        onClick={() => mutation.mutate()}
      >
        Save
      </Button>
    </div>
  );
}

function EntryDetails({ data: entry, meta }) {
  return (
    <div className="gl-entry-details">
      <Row className="g-3">
        <Col lg="4">
          <div className="gl-panel">
            <h6 className="gl-panel-title">Movement</h6>
            <dl className="gl-dl">
              <dt>Label</dt>
              <dd>{entry.label}</dd>
              {entry.details && (
                <>
                  <dt>Details</dt>
                  <dd className="gl-pre">{entry.details}</dd>
                </>
              )}
              <dt>Counterparty</dt>
              <dd>{entry.counterparty || "—"}</dd>
              <dt>Reference</dt>
              <dd>{entry.reference || "—"}</dd>
              <dt>VAT</dt>
              <dd>{entry.vat_amount ? `${formatAmount(entry.vat_amount, entry.currency_code)} ${entry.currency_code}` : "—"}</dd>
              <dt>Company</dt>
              <dd>{entry.company_name || "Unlinked"}</dd>
              <dt>Status</dt>
              <dd>
                {entry.is_locked ? <Badge color="success">Reconciled (locked)</Badge> : <Badge color="warning">Not locked</Badge>}
                {entry.needs_review && <Badge color="danger" className="ms-1">Needs review</Badge>}
              </dd>
              <dt>Category</dt>
              <dd>
                <CategoryEditor entry={entry} categories={meta?.categories} canEdit={can(meta, PERMISSIONS.ANNOTATE)} />
              </dd>
            </dl>
          </div>
        </Col>
        <Col lg="4">
          <EntryNotesPanel entryId={entry.id} canAnnotate={can(meta, PERMISSIONS.ANNOTATE)} isAdmin={meta?.is_admin} />
        </Col>
        <Col lg="4">
          <EntryAttachmentsPanel
            entryId={entry.id}
            canAttach={can(meta, PERMISSIONS.ATTACH)}
            isAdmin={meta?.is_admin}
            maxKb={meta?.max_attachment_kb}
          />
        </Col>
      </Row>
    </div>
  );
}

export default function LedgerTable({
  meta,
  rows,
  total,
  loading,
  page,
  perPage,
  sort = "booking_date",
  direction = "desc",
  onPageChange,
  onPerPageChange,
  onSort,
}) {
  const columns = useMemo(
    () => [
      {
        name: "Date",
        sortable: true,
        sortField: "booking_date",
        width: "112px",
        cell: (r) => (
          <div className="gl-date">
            <div className="gl-cell-strong" title={`Booking date ${r.booking_date}`}>{formatShortDate(r.booking_date)}</div>
            {r.value_date && r.value_date !== r.booking_date && (
              <div className="gl-muted" title={`Value date ${r.value_date}`}>
                val. {formatShortDate(r.value_date, r.value_date.slice(0, 4) !== String(r.booking_date).slice(0, 4))}
              </div>
            )}
          </div>
        ),
      },
      {
        name: "Description",
        grow: 4,
        minWidth: "200px",
        sortable: true,
        sortField: "label",
        cell: (r) => (
          <div className="gl-desc">
            <div className="gl-cell-strong" title={r.label}>{r.label}</div>
            <div className="gl-muted gl-desc-meta">
              {r.bank_profile ? (
                <span title={`${r.bank_profile.label} · ${r.company_name || ""}`}>
                  {r.bank_profile.label} ****{r.bank_profile.account_last4}
                  {r.company_name ? ` · ${r.company_name}` : ""}
                </span>
              ) : (
                <Badge color="warning">Unlinked</Badge>
              )}
              {r.counterparty && <span title={r.counterparty}> · {r.counterparty}</span>}
            </div>
          </div>
        ),
      },
      {
        name: "Type",
        minWidth: "110px",
        maxWidth: "150px",
        sortable: true,
        sortField: "operation_type",
        hide: "md",
        cell: (r) => (
          <div className="gl-type">
            <span className="gl-type-badge">{operationLabel(r.operation_type)}</span>
            {r.category && <span className="gl-category" title="Category">{r.category}</span>}
          </div>
        ),
      },
      {
        name: "Debit",
        right: true,
        minWidth: "100px",
        maxWidth: "140px",
        sortable: true,
        sortField: "debit",
        cell: (r) => (Number(r.debit) > 0 ? <span className="gl-amount text-danger">{formatAmount(r.debit, r.currency_code)}</span> : ""),
      },
      {
        name: "Credit",
        right: true,
        minWidth: "100px",
        maxWidth: "140px",
        sortable: true,
        sortField: "credit",
        cell: (r) => (Number(r.credit) > 0 ? <span className="gl-amount text-success">{formatAmount(r.credit, r.currency_code)}</span> : ""),
      },
      {
        name: "Balance",
        right: true,
        minWidth: "110px",
        maxWidth: "160px",
        sortable: true,
        sortField: "balance",
        hide: "sm",
        cell: (r) => (
          <span className="gl-amount">
            {formatAmount(r.balance, r.currency_code)} <span className="gl-muted">{r.currency_code}</span>
          </span>
        ),
      },
      {
        id: "extras",
        name: "",
        width: "56px",
        cell: (r) => (
          <div className="gl-row-icons">
            {r.notes_count > 0 && (
              <span title={`${r.notes_count} note(s)`}>
                <MessageSquare size={12} /> {r.notes_count}
              </span>
            )}
            {r.attachments_count > 0 && (
              <span title={`${r.attachments_count} attachment(s)`}>
                <Paperclip size={12} /> {r.attachments_count}
              </span>
            )}
          </div>
        ),
      },
    ].map((column) => (column.sortField ? { ...column, id: column.sortField } : column)),
    []
  );

  const [wrapperRef, stickyStyle] = useStickyTableHeader();

  return (
    <div ref={wrapperRef} style={stickyStyle}>
      <DataTable
        className="gl-table"
        columns={columns}
        customStyles={TABLE_STYLES}
        defaultSortFieldId={sort}
        defaultSortAsc={direction === "asc"}
        data={rows}
        keyField="id"
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={total}
        paginationDefaultPage={page}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[25, 50, 100, 200]}
        onChangePage={onPageChange}
        onChangeRowsPerPage={(size) => onPerPageChange(size)}
        sortServer
        onSort={(column, direction) => column.sortField && onSort(column.sortField, direction)}
        expandableRows
        expandOnRowClicked
        expandableRowsComponent={EntryDetails}
        expandableRowsComponentProps={{ meta }}
        conditionalRowStyles={[{ when: (r) => r.needs_review, classNames: ["gl-row-review"] }]}
        highlightOnHover
        pointerOnHover
        dense
        responsive={false}
        noDataComponent={<div className="p-4 gl-muted">No movements found. Upload a bank statement to get started.</div>}
      />
    </div>
  );
}
